// ============================================================
// CLUBHUB – Gruppen: kleine Zeichen (Art, Farbe, Zustand)
// ============================================================
// Die Kennfarbe einer Gruppe ist ein DATENWERT (`Group.color`). Sie
// kommt wie die Personenfarbe ueber `--person-color` ins Bild
// (`lib/person-colors.ts`) — nie als Hex im Markup.
// ============================================================
import {
  ClipboardList,
  HandCoins,
  Heart,
  Landmark,
  Network,
  Shirt,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import type { GroupKind } from "@/generated/prisma/enums";
import { GROUP_KIND } from "@/lib/labels";
import { avatarColorStyle } from "@/lib/person-colors";
import { cn } from "@/lib/utils";
import { TonBadge } from "@/components/mitglieder/status-badge";
import { endetBald, stand } from "@/lib/gruppen/logik";
import type { ZugehoerigkeitView } from "@/lib/gruppen/views";
import { formatDate } from "@/lib/utils";

export const ART_ZEICHEN: Record<GroupKind, LucideIcon> = {
  TEAM: Shirt,
  DEPARTMENT: Network,
  BOARD: Landmark,
  COMMITTEE: ClipboardList,
  SPONSOR: HandCoins,
  FAN: Heart,
  OTHER: UsersRound,
};

/** Der Punkt in der Kennfarbe — ohne Farbe ein Ring. */
export function Farbpunkt({ farbe, className }: { farbe: string | null; className?: string }) {
  return (
    <span
      aria-hidden
      style={avatarColorStyle(farbe)}
      className={cn(
        "inline-block h-2.5 w-2.5 shrink-0 rounded-full",
        farbe ? "bg-[var(--person-color)]" : "ring-border-strong ring-1 ring-inset",
        className,
      )}
    />
  );
}

/** Das Zeichen der Gruppe auf getoenter Flaeche — fuer Kopf und Karten. */
export function GruppenMarke({
  kind,
  farbe,
  groesse = "md",
}: {
  kind: GroupKind;
  farbe: string | null;
  groesse?: "sm" | "md";
}) {
  const Zeichen = ART_ZEICHEN[kind];
  return (
    <span
      aria-hidden
      style={avatarColorStyle(farbe)}
      className={cn(
        "grid shrink-0 place-items-center rounded-lg",
        groesse === "md" ? "h-9 w-9" : "h-7 w-7",
        farbe
          ? "bg-[color:color-mix(in_oklab,var(--person-color)_16%,transparent)] text-[color:color-mix(in_oklab,var(--person-color)_62%,black)] dark:bg-[color:color-mix(in_oklab,var(--person-color)_30%,transparent)] dark:text-[color:color-mix(in_oklab,var(--person-color)_55%,white)]"
          : "bg-bg-tertiary text-text-tertiary",
      )}
    >
      <Zeichen className={groesse === "md" ? "h-4 w-4" : "h-3.5 w-3.5"} />
    </span>
  );
}

/** „Mannschaft“ mit Zeichen, leise. */
export function ArtText({ kind, className }: { kind: GroupKind; className?: string }) {
  const Zeichen = ART_ZEICHEN[kind];
  return (
    <span className={cn("text-text-secondary inline-flex items-center gap-1.5 whitespace-nowrap", className)}>
      <Zeichen className="text-text-tertiary h-3.5 w-3.5 shrink-0" aria-hidden />
      {GROUP_KIND[kind]}
    </span>
  );
}

/** Zustand einer Zugehoerigkeit als Etikett. */
export function StandEtikett({ z, heute }: { z: Pick<ZugehoerigkeitView, "since" | "until">; heute: string }) {
  const s = stand(z, heute);
  if (s === "kuenftig") return <TonBadge ton="info">Ab {formatDate(z.since)}</TonBadge>;
  if (s === "beendet") return <TonBadge ton="neutral">Beendet</TonBadge>;
  if (endetBald(z, heute)) return <TonBadge ton="warning">Endet {formatDate(z.until)}</TonBadge>;
  if (z.until) return <TonBadge ton="success">Bis {formatDate(z.until)}</TonBadge>;
  return <TonBadge ton="success">Aktuell</TonBadge>;
}
